import _inherits from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
import _classCallCheck from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src455542086/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import View from './view.js';
import '../theme/components/arialiveannouncer/arialiveannouncer.css';
/**
 * The politeness level of an `aria-live` announcement.
 *
 * Available keys are:
 * * `AriaLiveAnnouncerPoliteness.POLITE`,
 * * `AriaLiveAnnouncerPoliteness.ASSERTIVE`
 *
 * [Learn more](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions#Politeness_levels).
 */
export var AriaLiveAnnouncerPoliteness = {
  POLITE: 'polite',
  ASSERTIVE: 'assertive'
};
/**
 * An accessibility helper that manages all ARIA live regions associated with an editor instance. ARIA live regions announce changes
 * to the state of the editor features.
 *
 * These announcements are consumed and propagated by screen readers and give users a better understanding of the current
 * state of the editor.
 *
 * To announce a state change to an editor feature named `'Some feature'`, use the {@link #announce} method:
 * ```ts
 * editor.ui.ariaLiveAnnouncer.announce( 'Some feature', 'Text of an announcement.' );
 * ```
 */
var AriaLiveAnnouncer = /*#__PURE__*/function () {
  /**
   * @inheritDoc
   */
  function AriaLiveAnnouncer(editor) {
    _classCallCheck(this, AriaLiveAnnouncer);
    this.editor = editor;
  }
  /**
   * Sets an announcement text to an aria region associated with a specific editor feature. The text is then
   * announced by a screen reader to the user.
   *
   * If the aria region of a given name does not exist, it will be created and can be re-used later. The name of the region
   * groups announcements originating from a specific editor feature and does not get announced by a screen reader.
   *
   * Using multiple regions allows for many announcements to be emitted in a short period of time. Changes to ARIA-live announcements
   * are captured by a screen reader and read out in the order they were emitted.
   *
   * The default announcement politeness level is `'polite'`.
   *
   * ```ts
   * // Most screen readers will queue announcements from multiple aria-live regions and read them out in the order they were emitted.
   * editor.ui.ariaLiveAnnouncer.announce( 'image', 'Image uploaded.' );
   * editor.ui.ariaLiveAnnouncer.announce( 'network', 'Connection lost. Reconnecting.' );
   * ```
   */
  _createClass(AriaLiveAnnouncer, [{
    key: "announce",
    value: function announce(regionName, announcementText) {
      var politeness = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : AriaLiveAnnouncerPoliteness.POLITE;
      var editor = this.editor;
      if (!this.view) {
        this.view = new AriaLiveAnnouncerView(editor.locale);
        editor.ui.view.body.add(this.view);
      }
      var regionView = this.view.regionViews.find(function (view) {
        return view.regionName === regionName;
      });
      if (!regionView) {
        regionView = new AriaLiveAnnouncerRegionView(this.view.locale);
        this.view.regionViews.add(regionView);
      }
      regionView.set({
        regionName: regionName,
        text: announcementText,
        politeness: politeness
      });
    }
  }]);
  return AriaLiveAnnouncer;
}();
/**
 * The view that aggregates all `aria-live` regions.
 */
export { AriaLiveAnnouncer as default };
export var AriaLiveAnnouncerView = /*#__PURE__*/function (_View) {
  _inherits(AriaLiveAnnouncerView, _View);
  var _super = _createSuper(AriaLiveAnnouncerView);
  function AriaLiveAnnouncerView(locale) {
    var _this;
    _classCallCheck(this, AriaLiveAnnouncerView);
    _this = _super.call(this, locale);
    _this.regionViews = _this.createCollection();
    _this.setTemplate({
      tag: 'div',
      attributes: {
        class: ['ck', 'ck-aria-live-announcer']
      },
      children: _this.regionViews
    });
    return _this;
  }
  return AriaLiveAnnouncerView;
}(View);
/**
 * The view that represents a single `aria-live` region (e.g. for a specific editor feature) and its text.
 */
export var AriaLiveAnnouncerRegionView = /*#__PURE__*/function (_View2) {
  _inherits(AriaLiveAnnouncerRegionView, _View2);
  var _super2 = _createSuper(AriaLiveAnnouncerRegionView);
  function AriaLiveAnnouncerRegionView(locale) {
    var _this2;
    _classCallCheck(this, AriaLiveAnnouncerRegionView);
    _this2 = _super2.call(this, locale);
    var bind = _this2.bindTemplate;
    _this2.set('regionName', '');
    _this2.set('text', '');
    _this2.set('politeness', AriaLiveAnnouncerPoliteness.POLITE);
    _this2.setTemplate({
      tag: 'div',
      attributes: {
        role: 'region',
        'data-region': bind.to('regionName'),
        'aria-live': bind.to('politeness')
      },
      children: [{
        text: bind.to('text')
      }]
    });
    return _this2;
  }
  return AriaLiveAnnouncerRegionView;
}(View);